// RITA-1498 (Nutzungserfassung Phase 1b) — Seitenaufruf und Seitenverlassen (Konzept 3.3, 4.1 Punkt 1).
// Beobachtet den Router; die Route wird immer als Muster gemeldet (services/routePattern).
// Beim Aktivieren gilt die aktuelle Seite als direkter Aufruf; jeder Wechsel meldet erst
// page_leave (sichtbare Dauer, Scrolltiefe) für die alte, dann page_view für die neue Seite.
import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import type { UsagePropValue } from 'global-intern-types';
import { decideNavType, InteractionHints } from '../services/navigation';
import { createPageTimer, PageTimer } from '../services/pageTiming';
import { toRoutePattern } from '../services/routePattern';
import type { UsageTracker } from '../services/tracker';

export interface PageViewTrackingDeps {
  tracker: UsageTracker;
  hints: InteractionHints;
  active: boolean;
  now?: () => number;
  win?: Window;
  doc?: Document;
}

export const usePageViewTracking = ({
  tracker,
  hints,
  active,
  now = () => Date.now(),
  win = window,
  doc = document,
}: PageViewTrackingDeps): void => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const timerRef = useRef<PageTimer | null>(null);
  const previousRouteRef = useRef<string | null>(null);

  // Die aktuelle Route kennt der Tracker immer (auch inaktiv), damit session_start sie hat.
  useEffect(() => {
    tracker.setRoute(toRoutePattern(location.pathname));
  }, [tracker, location.pathname]);

  useEffect(() => {
    if (!active) {
      previousRouteRef.current = null;
      return undefined;
    }
    const route = toRoutePattern(location.pathname);
    if (timerRef.current === null) {
      timerRef.current = createPageTimer({
        now,
        isVisible: () => doc.visibilityState !== 'hidden',
        getScrollMetrics: () => ({
          scrollY: win.scrollY,
          innerHeight: win.innerHeight,
          scrollHeight: doc.documentElement ? doc.documentElement.scrollHeight : 0,
        }),
      });
    }
    const timer = timerRef.current;
    const previousRoute = previousRouteRef.current;
    const props: Record<string, UsagePropValue> = {
      nav_type: decideNavType({
        isFirst: previousRoute === null,
        navigationType,
        lastClick: hints.lastClick(),
        now: now(),
      }),
    };
    if (previousRoute !== null) props.prev_route = previousRoute;
    tracker.record({ type: 'page_view', route, props });
    timer.start();
    previousRouteRef.current = route;

    const onScroll = (): void => timer.onScroll();
    const onVisibilityChange = (): void => timer.onVisibilityChange();
    win.addEventListener('scroll', onScroll, { passive: true });
    doc.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      win.removeEventListener('scroll', onScroll);
      doc.removeEventListener('visibilitychange', onVisibilityChange);
      const { visibleMs, scrollPct } = timer.finish();
      tracker.record({ type: 'page_leave', route, value: visibleMs, props: { scroll_pct: scrollPct } });
    };
    // navigationType gehört bewusst nicht in die Abhängigkeiten: es beschreibt den Wechsel, der
    // location.pathname geändert hat, und soll keinen eigenen Durchlauf auslösen.
  }, [active, location.pathname, tracker, hints, now, win, doc]);
};

export default usePageViewTracking;
